import React, { forwardRef, memo, useMemo } from 'react';
import { DynamicWidgetsProvided, RefinementListProvided } from 'react-instantsearch-core';
import { connectDynamicWidgets, connectRefinementList } from 'react-instantsearch-dom';
import { FilterGroupAttributes } from '~/features/productsList/components/Filters/FiltersGroup';
import { TranslationKey } from '~/lib/data-contract';
import { Accordion, AccordionItem, Text } from '~/shared/components';
import { useTranslation } from '~/shared/utils';

interface SingleFilterItemProps extends RefinementListProvided {
    filterName: string;
    title: string;
    index: number;
}

// Beware! This component can throw a warning about not being able to access the ref property, even though we are using
// forwardRef. I might've missed something, but it works just fine, even with keyboard navigation, so the parent accordion
// component isn't annoyed at all.
const SingleFilterItem = memo(
    forwardRef<HTMLButtonElement, SingleFilterItemProps>(
        ({ filterName, title, index, items }, ref) => {
            const { translate } = useTranslation();

            // If we have no filters at all in this group, abort at once.
            if (!items.length) return <></>;

            return (
                <AccordionItem
                    key={filterName}
                    id={`filter-drawer-${index.toString()}`}
                    variant="filter"
                    header={<Text variant="body" children={translate(title as TranslationKey)} />}
                    ref={ref}
                >
                    <FilterGroupAttributes
                        key={filterName}
                        attribute={filterName}
                        showMore
                        showMoreLimit={100}
                    />
                </AccordionItem>
            );
        }
    )
);
const ConnectedSingleFilterItem = connectRefinementList(SingleFilterItem);
ConnectedSingleFilterItem.displayName = 'ConnectedSingleFilterItem';

export const ConnectedAccordionGroups = ({ attributesToRender }: DynamicWidgetsProvided) => {
    const filters = useMemo(() => {
        return attributesToRender.map((filter) => {
            return {
                name: filter,
                title: `commerce.filters.${filter}`,
            };
        });
    }, [attributesToRender]);

    return (
        <Accordion type="single">
            {filters.map(({ name, title }: { name: string; title: string }, index) => (
                <ConnectedSingleFilterItem
                    attribute={name}
                    key={name}
                    {...{ filterName: name, title, index }}
                />
            ))}
        </Accordion>
    );
};

export const AccordionGroups = connectDynamicWidgets(ConnectedAccordionGroups);
